import { useEffect, useRef } from "react";

const highlights = [
  {
    icon: "ri-heart-pulse-line",
    title: "Atendimento Humanizado",
    desc: "Cada cliente recebe atenção personalizada, com escuta ativa e cuidado genuíno em cada etapa.",
  },
  {
    icon: "ri-scales-3-line",
    title: "Atuação Estratégica",
    desc: "Soluções jurídicas personalizadas, com foco em resultados efetivos e proteção real dos seus direitos.",
  },
  {
    icon: "ri-hospital-line",
    title: "Especialização em Saúde",
    desc: "Referência em Direito Médico e Direito à Saúde, defendendo profissionais e pacientes.",
  },
  {
    icon: "ri-team-line",
    title: "Equipe Própria e Parceiros",
    desc: "Equipe qualificada com rede de parceiros especializados para cobertura completa em todas as áreas.",
  },
];

export default function Sobre() {
  const sectionRef = useRef<HTMLElement>(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            entry.target.querySelectorAll(".fade-in-item").forEach((el, i) => {
              setTimeout(() => {
                (el as HTMLElement).style.opacity = "1";
                (el as HTMLElement).style.transform = "translateY(0)";
              }, i * 150);
            });
          }
        });
      },
      { threshold: 0.15 }
    );
    if (sectionRef.current) observer.observe(sectionRef.current);
    return () => observer.disconnect();
  }, []);

  return (
    <section id="sobre" ref={sectionRef} className="py-24 bg-[#F2F2F2]">
      <div className="max-w-7xl mx-auto px-6">
        {/* Section Header */}
        <div
          className="text-center mb-16 fade-in-item"
          style={{ opacity: 0, transform: "translateY(20px)", transition: "opacity 0.7s ease, transform 0.7s ease" }}
        >
          <div className="inline-flex items-center gap-3 mb-4">
            <div className="w-10 h-px bg-[#C8A63A]" />
            <span
              className="text-xs font-semibold tracking-[0.3em] uppercase text-[#C8A63A]"
              style={{ fontFamily: "'Inter', sans-serif" }}
            >
              Quem Somos
            </span>
            <div className="w-10 h-px bg-[#C8A63A]" />
          </div>
          <h2
            className="text-3xl md:text-4xl font-bold text-[#0A2472] mb-4"
            style={{ fontFamily: "'Cormorant Garamond', serif" }}
          >
            Advocacia com propósito e excelência
          </h2>
          <p
            className="text-gray-600 max-w-2xl mx-auto text-base leading-relaxed"
            style={{ fontFamily: "'Inter', sans-serif" }}
          >
            A QM Advocacia nasceu do compromisso de oferecer advocacia de alto padrão, acessível e eficiente, com especialização em saúde jurídica.
          </p>
        </div>

        {/* Main Content */}
        <div className="grid md:grid-cols-2 gap-16 items-center">
          {/* Text */}
          <div
            className="fade-in-item"
            style={{ opacity: 0, transform: "translateY(20px)", transition: "opacity 0.7s ease, transform 0.7s ease" }}
          >
            <h3
              className="text-2xl md:text-3xl font-bold text-[#0A2472] mb-6 leading-snug"
              style={{ fontFamily: "'Cormorant Garamond', serif" }}
            >
              Quelen Manske Sociedade Individual de Advocacia
            </h3>
            <div className="space-y-4 text-gray-600 leading-relaxed text-sm" style={{ fontFamily: "'Inter', sans-serif" }}>
              <p>
                O escritório <strong className="text-[#0A2472]">Quelen Manske Advocacia – QM</strong> atende ao público em geral, com especial foco em profissionais da saúde e pacientes, oferecendo representação jurídica completa nas áreas de Direito Médico, Direito à Saúde, Direito Previdenciário, Trabalhista, de Família e muito mais.
              </p>
              <p>
                Com sede em Joinville/SC, o escritório combina <strong className="text-[#0A2472]">atuação estratégica</strong>, equipe própria e rede de parceiros especializados para garantir a melhor solução para cada caso.
              </p>
              <p>
                Nossa missão é oferecer advocacia de excelência, célere e eficiente, garantindo a efetivação dos direitos dos clientes com <strong className="text-[#0A2472]">ética, transparência e responsabilidade</strong>.
              </p>
            </div>

            {/* Valores Chips */}
            <div className="flex flex-wrap gap-2 mt-8 justify-center md:justify-start">
              {["Ética", "Transparência", "Compromisso", "Parceria", "Responsabilidade"].map((v) => (
                <span
                  key={v}
                  className="px-4 py-1.5 rounded-full text-xs font-semibold border border-[#1F4ED8]/20 text-[#1F4ED8] bg-[#1F4ED8]/5"
                  style={{ fontFamily: "'Inter', sans-serif" }}
                >
                  {v}
                </span>
              ))}
            </div>
          </div>

          {/* Image */}
          <div
            className="fade-in-item relative"
            style={{ opacity: 0, transform: "translateY(20px)", transition: "opacity 0.7s ease, transform 0.7s ease" }}
          >
            <div
              className="relative w-full rounded-lg overflow-hidden flex items-end justify-center"
              style={{ background: "#f0f0f0" }}
            >
              <img
                src="https://storage.readdy-site.link/project_files/69b9a406-2716-472b-9d76-26d620cf6477/2c19ea14-7f54-42d5-9224-2997d459e82d_foto-quelen.png?v=51ee61fcf572d2444e7f3effe2d94d86"
                alt="Dra. Quelen Manske"
                className="w-full h-auto object-contain"
                style={{ display: "block" }}
              />
              <div className="absolute inset-0 bg-gradient-to-t from-[#0A2472]/20 to-transparent pointer-events-none" />
            </div>

            {/* Gold accent card */}
            <div
              className="absolute -bottom-6 -left-6 rounded-lg p-5 text-white"
              style={{ background: "linear-gradient(135deg, #0A2472, #1F4ED8)" }}
            >
              <div
                className="text-3xl font-bold"
                style={{ color: "#E6D18A", fontFamily: "'Cormorant Garamond', serif" }}
              >
                OAB/SC
              </div>
              <div className="text-xs text-white/80 mt-1" style={{ fontFamily: "'Inter', sans-serif" }}>
                Registro ativo e regularizado
              </div>
            </div>

            {/* Decorative border */}
            <div className="absolute -top-4 -right-4 w-24 h-24 rounded-lg border-2 border-[#C8A63A]/40" />
          </div>
        </div>
      </div>
    </section>
  );
}
